import { useEffect, useRef } from 'react'

interface SlideUpDialogHandlers {
  dialogRef: React.RefObject<HTMLDialogElement | null>
  closeBtnRef: React.RefObject<HTMLButtonElement | null>
  requestClose: () => void
  handleCancel: (e: React.SyntheticEvent) => void
  handleDialogClick: (e: React.MouseEvent<HTMLDialogElement>) => void
}

export function useSlideUpDialog<T>(
  openValue: T | null | undefined,
  onClose: () => void,
): SlideUpDialogHandlers {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (openValue) {
      dialog.showModal()
      closeBtnRef.current?.focus()
    }
  }, [openValue])

  // Play slide-down, then close dialog and notify parent
  function requestClose() {
    const dialog = dialogRef.current
    if (!dialog || dialog.hasAttribute('data-closing')) return

    dialog.setAttribute('data-closing', '')
    dialog.addEventListener(
      'animationend',
      () => {
        dialog.removeAttribute('data-closing')
        dialog.close()
        onClose()
      },
      { once: true },
    )
  }

  // Intercept Escape so it goes through our animation too
  function handleCancel(e: React.SyntheticEvent) {
    e.preventDefault()
    requestClose()
  }

  function handleDialogClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === dialogRef.current) requestClose()
  }

  return { dialogRef, closeBtnRef, requestClose, handleCancel, handleDialogClick }
}
